import React, { useEffect, useState } from 'react';
import { Zap, Heart, Brain, Bone, Baby, Users, Stethoscope, Activity, ArrowRight, ChevronDown, CheckCircle, X, MessageCircle } from 'lucide-react';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { buildWhatsappLink } from '../../utils/whatsapp';

const Services = () => {
  const [activeService, setActiveService] = useState(0);
  const [showServiceDetail, setShowServiceDetail] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);
  const isMobile = useMediaQuery('(max-width: 639px)');

  const services = [
    {
      icon: Activity,
      title: "Sports Rehabilitation",
      description: "Injury assessment, performance enhancement, on-field support for athletes and teams.",
      features: ["Injury Assessment", "Performance Enhancement", "On-field Support", "Return-to-Play Protocols"],
      image: "https://images.pexels.com/photos/4506109/pexels-photo-4506109.jpeg?auto=compress&cs=tinysrgb&w=600",
      color: "from-red-500 to-red-600",
      bgColor: "bg-red-50",
      textColor: "text-red-700"
    },
    {
      icon: Bone,
      title: "Orthopedic Physiotherapy",
      description: "Spine & disc conditions, post-fracture stiffness, joint pain, back/neck pain.",
      features: ["Spine & Disc Conditions", "Post-Fracture Stiffness", "Joint Pain", "Back/Neck Pain"],
      image: "https://images.pexels.com/photos/4386467/pexels-photo-4386467.jpeg?auto=compress&cs=tinysrgb&w=600",
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700"
    },
    {
      icon: Brain,
      title: "Neurological Rehabilitation",
      description: "Stroke, paralysis, Parkinson's, multiple sclerosis, and more.",
      features: ["Stroke Rehab", "Paralysis Management", "Parkinson's", "Multiple Sclerosis"],
      image: "https://images.pexels.com/photos/4491459/pexels-photo-4491459.jpeg?auto=compress&cs=tinysrgb&w=600",
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700"
    },
    {
      icon: Heart,
      title: "Cardiopulmonary Therapy",
      description: "Cardiac rehab, pulmonary function improvement, post-surgical care.",
      features: ["Cardiac Rehab", "Pulmonary Function", "Post-Surgical Care", "Breathing Exercises"],
      image: "https://images.pexels.com/photos/4506106/pexels-photo-4506106.jpeg?auto=compress&cs=tinysrgb&w=600",
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-50",
      textColor: "text-green-700"
    },
    {
      icon: Baby,
      title: "Pediatric Physiotherapy",
      description: "Developmental delays, cerebral palsy, congenital conditions.",
      features: ["Developmental Delays", "Cerebral Palsy", "Congenital Conditions", "Motor Skills Development"],
      image: "https://images.pexels.com/photos/8613061/pexels-photo-8613061.jpeg?auto=compress&cs=tinysrgb&w=600",
      color: "from-pink-500 to-pink-600",
      bgColor: "bg-pink-50",
      textColor: "text-pink-700"
    },
    {
      icon: Users,
      title: "Geriatric Care",
      description: "Balance training, osteoarthritis management, fall prevention for seniors.",
      features: ["Balance Training", "Osteoarthritis Management", "Fall Prevention", "Mobility Enhancement"],
      image: "https://images.pexels.com/photos/7551544/pexels-photo-7551544.jpeg?auto=compress&cs=tinysrgb&w=600",
      color: "from-orange-500 to-orange-600",
      bgColor: "bg-orange-50",
      textColor: "text-orange-700"
    },
    {
      icon: Stethoscope,
      title: "Oncology Rehabilitation",
      description: "Cancer pain management, post-surgical mobility, lymphedema care.",
      features: ["Cancer Pain Management", "Post-Surgical Mobility", "Lymphedema Care", "Quality of Life Improvement"],
      image: "https://images.pexels.com/photos/4386467/pexels-photo-4386467.jpeg?auto=compress&cs=tinysrgb&w=600",
      color: "from-teal-500 to-teal-600",
      bgColor: "bg-teal-50",
      textColor: "text-teal-700"
    },
    {
      icon: Zap,
      title: "Manual Therapies & Modalities",
      description: "Soft tissue mobilization, joint mobilization, dry needling, acupuncture, TENS, ultrasound, hydrotherapy, taping, orthotics, and more.",
      features: ["Soft Tissue Mobilization", "Joint Mobilization", "Dry Needling & Acupuncture", "TENS, Ultrasound, Interferential Therapy", "Hydrotherapy & Exercise", "Kinesio Taping & Orthotics", "Home Care & Tele-Physio"],
      image: "https://images.pexels.com/photos/4506106/pexels-photo-4506106.jpeg?auto=compress&cs=tinysrgb&w=600",
      color: "from-indigo-500 to-indigo-600",
      bgColor: "bg-indigo-50",
      textColor: "text-indigo-700"
    }
  ];

  // Example FAQs for each service (static, can be expanded)
  const serviceFAQs = [
    [
      { q: "What sports injuries do you treat?", a: "We treat sprains, strains, ligament injuries, fractures, and more for athletes of all levels." },
      { q: "Do you offer on-field support?", a: "Yes, we provide on-field physiotherapy and injury management for teams and events." }
    ],
    [
      { q: "Can you help with chronic back pain?", a: "Yes, our orthopedic physiotherapy includes manual therapy, exercise, and modalities for back and neck pain." },
      { q: "What is post-fracture rehab?", a: "We help restore mobility and strength after fractures or orthopedic surgery." }
    ],
    [
      { q: "Do you treat stroke and paralysis?", a: "Yes, we offer comprehensive neuro-rehab for stroke, paralysis, Parkinson's, and more." },
      { q: "Is therapy suitable for MS?", a: "Our team is experienced in managing multiple sclerosis and other neurological conditions." }
    ],
    [
      { q: "What is cardiac rehab?", a: "A program to improve heart health after cardiac events or surgery, including exercise and education." },
      { q: "Do you help with breathing issues?", a: "Yes, we offer pulmonary rehab and breathing exercises for lung conditions." }
    ],
    [
      { q: "What pediatric conditions do you treat?", a: "We help with developmental delays, cerebral palsy, and congenital conditions in children." },
      { q: "Is therapy play-based?", a: "Yes, we use play and fun activities to engage children in therapy." }
    ],
    [
      { q: "Can you help seniors with balance?", a: "Yes, we offer fall prevention, balance training, and osteoarthritis management for seniors." },
      { q: "Do you offer home visits?", a: "Yes, home care is available for geriatric and immobile patients." }
    ],
    [
      { q: "What is oncology rehab?", a: "Rehabilitation for cancer patients to manage pain, mobility, and quality of life during and after treatment." },
      { q: "Do you treat lymphedema?", a: "Yes, we provide lymphedema care and post-surgical mobility support." }
    ],
    [
      { q: "What are manual therapies?", a: "Hands-on techniques like joint and soft tissue mobilization, dry needling, and more." },
      { q: "Do you offer electrotherapy?", a: "Yes, we use TENS, ultrasound, and other modalities as needed." }
    ]
  ];

  // Example process steps for each service
  const serviceProcess = [
    ["Assessment & Diagnosis", "Personalized Treatment Plan", "On-field/Clinic Therapy", "Performance Tracking"],
    ["Assessment", "Manual Therapy & Modalities", "Exercise Prescription", "Progress Review"],
    ["Neuro Assessment", "Goal Setting", "Therapy Sessions", "Home Program"],
    ["Cardiac/Pulmonary Assessment", "Exercise & Breathing Training", "Education", "Follow-up"],
    ["Pediatric Assessment", "Play-based Therapy", "Parental Guidance", "Progress Tracking"],
    ["Geriatric Assessment", "Balance & Mobility Training", "Pain Management", "Home Integration"],
    ["Oncology Assessment", "Pain & Mobility Management", "Lymphedema Care", "Quality of Life Support"],
    ["Assessment", "Manual/Electrotherapy", "Exercise/Modalities", "Home/Tele-Physio"],
  ];

  // Function to handle Learn More button click
  const handleLearnMore = (index: number) => {
    setActiveService(index);
    setShowServiceDetail(true);
    if (!isMobile) {
      // desktop: scroll to detail
      setTimeout(() => {
        const serviceDetail = document.getElementById('service-detail');
        if (serviceDetail) {
          serviceDetail.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        }
      }, 100);
    }
  };

  // Lock body scroll when mobile modal is open
  useEffect(() => {
    if (isMobile && showServiceDetail) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isMobile, showServiceDetail]);

  return (
    <section id="services" className="pt-14 sm:pt-16 lg:pt-20 pb-8 sm:pb-10 lg:pb-12 bg-gradient-to-br from-gray-50 to-white scroll-mt-28 sm:scroll-mt-32 lg:scroll-mt-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 lg:mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-4 lg:mb-6 leading-tight">
            Comprehensive Physiotherapy Services
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4 leading-relaxed">
            From sports injuries to neurological rehabilitation, we provide specialized care 
            across all physiotherapy disciplines with state-of-the-art equipment and techniques.
          </p>
        </div>

        {/* Services - Responsive layout */}
        {!isMobile ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 mb-8 lg:mb-10">
            {services.map((service, index) => (
              <div
                key={index}
                className={`group cursor-pointer transition-all duration-500 transform hover-lift-enhanced ${
                  activeService === index 
                    ? 'scale-105 -translate-y-2 shadow-2xl z-10' 
                    : 'hover:scale-105 hover:-translate-y-2 hover:shadow-xl'
                }`}
                onClick={() => setActiveService(index)}
              >
                <div className={`relative overflow-hidden rounded-2xl bg-white border border-gray-200 h-full transition-all duration-500 card-hover-enhanced ${
                  activeService === index ? 'ring-4 ring-blue-500/20 shadow-2xl' : 'shadow-lg hover:shadow-xl'
                }`}>
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-90 group-hover:opacity-95 transition-opacity duration-300`}></div>
                  <div className="relative p-4 sm:p-5 lg:p-6 h-full flex flex-col justify-between">
                    <div className="mb-3 sm:mb-4">
                      <div className={`inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-xl bg-white/20 backdrop-blur-sm mb-3 transition-transform duration-300 group-hover:scale-110`}>
                        {React.createElement(service.icon, { className: "w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" })}
                      </div>
                      <h3 className="text-sm sm:text-base lg:text-lg font-bold text-white mb-2 leading-tight">
                        {service.title}
                      </h3>
                    </div>
                    <p className="text-white/90 text-xs sm:text-sm lg:text-base mb-3 leading-relaxed">{service.description}</p>
                    <div className="mt-auto">
                      <button
                        className="group/btn inline-flex items-center text-white font-medium text-xs focus:outline-none focus:ring-1 focus:ring-white/50 rounded-lg transition-all duration-300 hover:bg-white/20 px-2 py-1.5 sm:px-3 sm:py-2 btn-animate-enhanced"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLearnMore(index);
                        }}
                      >
                        <span>Learn More</span>
                        <ArrowRight className="ml-1.5 w-3 h-3 sm:w-4 sm:h-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Mobile: horizontal scroller of compact cards
          <div className="relative mb-6">
            <div className="flex gap-3 overflow-x-auto pb-1 snap-x snap-mandatory">
              {services.map((service, index) => (
                <button
                  key={index}
                  onClick={() => handleLearnMore(index)}
                  className="min-w-[200px] snap-start bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow text-left"
                >
                  <div className={`h-24 rounded-t-2xl bg-gradient-to-br ${service.color} relative`}>
                    <div className="absolute bottom-2 left-2 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm">
                      {React.createElement(service.icon, { className: 'w-5 h-5 text-white' })}
                    </div>
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-bold text-gray-900 leading-snug line-clamp-2">{service.title}</h3>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Service Detail - Only shows when Learn More is clicked */}
        {showServiceDetail && !isMobile && (
          <div id="service-detail" className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 mb-8">
            <div className="grid grid-cols-1 xl:grid-cols-2">
              {/* Content Section - Ultra compact */}
              <div className="p-4 sm:p-6 lg:p-8 order-2 xl:order-1">
                {/* Service Header - Ultra compact */}
                <div className="flex flex-col sm:flex-row sm:items-center mb-4 gap-3">
                  <div className={`p-2.5 rounded-lg bg-gradient-to-br ${services[activeService].color} shadow-md flex-shrink-0`}>
                    {React.createElement(services[activeService].icon, { className: "w-5 h-5 text-white" })}
                  </div>
                  <h3 className="text-base lg:text-lg xl:text-xl font-bold text-gray-900 leading-tight">
                    {services[activeService].title}
                  </h3>
                </div>

                {/* Description - Ultra compact */}
                <p className="text-sm lg:text-base text-gray-600 mb-4 leading-relaxed">
                  {services[activeService].description}
                </p>

                {/* Key Features - Ultra compact */}
                <div className="mb-4">
                  <h4 className="text-sm lg:text-base font-bold text-gray-900 mb-3">Key Features</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {services[activeService].features.map((feature, index) => (
                      <div key={index} className="flex items-center p-2 rounded-md bg-gray-50 hover:bg-gray-100 transition-all duration-200">
                        <CheckCircle className="w-3.5 h-3.5 text-green-600 mr-2 flex-shrink-0" />
                        <span className="text-xs text-gray-700 font-medium">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Process Steps - Ultra compact */}
                <div className="mb-4">
                  <h4 className="text-sm lg:text-base font-bold text-gray-900 mb-3">How It Works</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {serviceProcess[activeService].map((step, idx) => (
                      <div key={idx} className="flex items-start p-2 rounded-md bg-blue-50 hover:bg-blue-100 transition-all duration-200">
                        <div className="flex-shrink-0 w-5 h-5 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xs mr-2 mt-0.5">
                          {idx + 1}
                        </div>
                        <span className="text-xs text-gray-700 font-medium">{step}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* FAQs - Ultra compact */}
                <div className="mb-4">
                  <h4 className="text-sm lg:text-base font-bold text-gray-900 mb-3">Frequently Asked Questions</h4>
                  <div className="space-y-2">
                    {serviceFAQs[activeService].map((faq, idx) => (
                        <div key={idx} className="border border-gray-200 rounded-md overflow-hidden hover:shadow-sm transition-shadow duration-200">
                          <div className="p-2.5">
                            <h5 className="font-semibold text-gray-900 text-xs mb-1.5">{faq.q}</h5>
                            <p className="text-gray-600 text-xs leading-relaxed">{faq.a}</p>
                          </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Buttons - Ultra compact */}
                <div className="flex flex-col sm:flex-row gap-2">
                  <button className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg flex-1 sm:flex-none btn-animate-enhanced focus-ring">
                  Book Consultation
                </button>
                  <button className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg flex-1 sm:flex-none btn-animate-enhanced focus-ring">
                  Learn More
                </button>
              </div>
            </div>

              {/* Image Section - Ultra compact */}
              <div className="relative order-1 xl:order-2 h-40 sm:h-48 lg:h-56 xl:h-auto min-h-[160px]">
              <img
                src={services[activeService].image}
                alt={services[activeService].title}
                className="w-full h-full object-cover"
                  loading="lazy"
              />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                  <h4 className="text-sm font-bold mb-1">{services[activeService].title}</h4>
                  <p className="text-white/90 text-xs">Professional care with advanced techniques</p>
            </div>
          </div>
        </div>
          </div>
        )}
        {/* Mobile Modal Detail */}
        {showServiceDetail && isMobile && (
          <div className="fixed inset-0 z-[80] bg-black/50" onClick={() => setShowServiceDetail(false)}>
            <div
              className="absolute inset-x-0 bottom-0 bg-white rounded-t-2xl shadow-2xl max-h-[85vh] overflow-y-auto"
              style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b">
                <h3 className="text-base font-bold text-gray-900">{services[activeService].title}</h3>
                <button onClick={() => setShowServiceDetail(false)} aria-label="Close" className="p-2 rounded-lg hover:bg-gray-100">
                  <X className="w-5 h-5 text-gray-700" />
                </button>
              </div>
              <div className="w-full h-40 overflow-hidden">
                <img src={services[activeService].image} alt={services[activeService].title} className="w-full h-full object-cover" />
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-700 mb-4">{services[activeService].description}</p>
                <h4 className="text-sm font-bold text-gray-900 mb-2">Key Features</h4>
                <div className="grid grid-cols-1 gap-2 mb-4">
                  {services[activeService].features.map((feature, index) => (
                    <div key={index} className="flex items-center p-2 rounded-lg bg-gray-50">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <a
                    href={buildWhatsappLink({ service: services[activeService].title, context: 'Service Modal' })}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-green-600 text-white py-2 rounded-lg font-semibold"
                  >
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                  </a>
                  <button className="flex-1 border border-blue-600 text-blue-600 py-2 rounded-lg font-semibold" onClick={() => setShowServiceDetail(false)}>Close</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Services;